import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package, Plus, Pencil, Trash2, RefreshCw, Loader2, ExternalLink, Search,
  Star, Layers, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { adminBundlesAPI } from '@/services/api';
import { toast } from 'sonner';

const blankForm = () => ({
  slug: '',
  title: '',
  subtitle: '',
  category: 'HR Compliance & Employment Law',
  is_active: true,
  is_featured: false,
  hero_eyebrow: 'Professional Development Bundle',
  hero_stats: [{ value: '', label: '' }],
  about_paragraphs: [''],
  benefits: [{ icon: '✨', title: '', description: '' }],
  audience: [{ icon: '👤', label: '' }],
  price_download: 0,
  price_usb: 0,
  original_price_download: '',
  original_price_usb: '',
  webinar_ids: [],
  seo_title: '',
  seo_description: '',
});

export default function AdminBundlesPage() {
  const [loading, setLoading] = useState(true);
  const [bundles, setBundles] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  // Webinar picker
  const [webinarOptions, setWebinarOptions] = useState([]);
  const [webinarSearch, setWebinarSearch] = useState('');
  const [loadingWebinars, setLoadingWebinars] = useState(false);

  const fetchBundles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminBundlesAPI.list({ page, per_page: 50, search: search || undefined });
      setBundles(res.data.bundles || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchBundles(); }, [fetchBundles]);

  const fetchWebinarOptions = useCallback(async () => {
    setLoadingWebinars(true);
    try {
      const res = await adminBundlesAPI.lookupWebinars({ search: webinarSearch || undefined, limit: 300 });
      setWebinarOptions(res.data.webinars || []);
    } catch {
      // ignore
    } finally {
      setLoadingWebinars(false);
    }
  }, [webinarSearch]);

  useEffect(() => {
    if (editorOpen) fetchWebinarOptions();
  }, [editorOpen, fetchWebinarOptions]);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm());
    setEditorOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await adminBundlesAPI.get(id);
      const b = res.data;
      setEditingId(id);
      setForm({
        slug: b.slug || '',
        title: b.title || '',
        subtitle: b.subtitle || '',
        category: b.category || 'General',
        is_active: b.is_active !== false,
        is_featured: !!b.is_featured,
        hero_eyebrow: b.hero_eyebrow || 'Professional Development Bundle',
        hero_stats: b.hero_stats?.length ? b.hero_stats : [{ value: '', label: '' }],
        about_paragraphs: b.about_paragraphs?.length ? b.about_paragraphs : [''],
        benefits: b.benefits?.length ? b.benefits : [{ icon: '✨', title: '', description: '' }],
        audience: b.audience?.length ? b.audience : [{ icon: '👤', label: '' }],
        price_download: b.price_download ?? 0,
        price_usb: b.price_usb ?? 0,
        original_price_download: b.original_price_download ?? '',
        original_price_usb: b.original_price_usb ?? '',
        webinar_ids: b.webinar_ids || [],
        seo_title: b.seo_title || '',
        seo_description: b.seo_description || '',
      });
      setEditorOpen(true);
    } catch {
      toast.error('Failed to load bundle');
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete bundle "${b.title}"? This cannot be undone.`)) return;
    try {
      await adminBundlesAPI.remove(b.id);
      toast.success('Bundle deleted');
      fetchBundles();
    } catch {
      toast.error('Failed to delete bundle');
    }
  };

  const buildPayload = () => ({
    slug: form.slug,
    title: form.title,
    subtitle: form.subtitle,
    category: form.category,
    is_active: !!form.is_active,
    is_featured: !!form.is_featured,
    hero_eyebrow: form.hero_eyebrow,
    hero_stats: form.hero_stats.filter((s) => s.value || s.label),
    about_paragraphs: form.about_paragraphs.filter((p) => (p || '').trim()),
    benefits: form.benefits.filter((b) => b.title || b.description),
    audience: form.audience.filter((a) => a.label),
    price_download: parseFloat(form.price_download) || 0,
    price_usb: parseFloat(form.price_usb) || 0,
    original_price_download: form.original_price_download === '' ? null : parseFloat(form.original_price_download),
    original_price_usb: form.original_price_usb === '' ? null : parseFloat(form.original_price_usb),
    webinar_ids: form.webinar_ids,
    seo_title: form.seo_title || null,
    seo_description: form.seo_description || null,
  });

  const handleSave = async () => {
    if (!form.title || !form.slug) {
      toast.error('Title and slug are required');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await adminBundlesAPI.update(editingId, payload);
        toast.success('Bundle updated');
      } else {
        await adminBundlesAPI.create(payload);
        toast.success('Bundle created');
      }
      setEditorOpen(false);
      fetchBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save bundle');
    } finally {
      setSaving(false);
    }
  };

  // Repeater helpers
  const updateRepeater = (key, idx, field, value) => {
    setForm((f) => {
      const arr = [...f[key]];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...f, [key]: arr };
    });
  };
  const addRepeaterRow = (key, blank) => setForm((f) => ({ ...f, [key]: [...f[key], blank] }));
  const removeRepeaterRow = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const toggleWebinar = (id) => {
    setForm((f) => ({
      ...f,
      webinar_ids: f.webinar_ids.includes(id)
        ? f.webinar_ids.filter((x) => x !== id)
        : [...f.webinar_ids, id],
    }));
  };

  const selectedWebinarCount = form.webinar_ids.length;

  const filteredWebinarOptions = useMemo(() => {
    if (!webinarSearch.trim()) return webinarOptions;
    const q = webinarSearch.toLowerCase();
    return webinarOptions.filter((w) =>
      (w.title || '').toLowerCase().includes(q) || (w.webinar_code || '').toLowerCase().includes(q)
    );
  }, [webinarOptions, webinarSearch]);

  return (
    <div className="space-y-6" data-testid="admin-bundles-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Bundles</h2>
          <p className="text-slate-500">Curated webinar bundles with Download Access & USB Flash Drive pricing</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchBundles} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} size="sm" data-testid="create-bundle-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Bundle
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search bundles by title, slug, or category..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="search-bundles"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-navy-500" />
            All Bundles ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : bundles.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No bundles yet</p>
              <Button onClick={openCreate} className="mt-4" size="sm">
                <Plus className="w-4 h-4 mr-2" /> Create your first bundle
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Webinars</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bundles.map((b) => (
                  <TableRow key={b.id} data-testid={`bundle-row-${b.slug}`}>
                    <TableCell>
                      <div className="font-medium text-navy-800">{b.title}</div>
                      <div className="text-xs text-slate-500 font-mono">/bundles/{b.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{b.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-navy-700">{b.webinar_count || 0}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>${b.price_download} <span className="text-xs text-slate-400">DL</span></div>
                        <div>${b.price_usb} <span className="text-xs text-slate-400">USB</span></div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {b.is_active
                          ? <Badge className="bg-green-100 text-green-700 w-fit">Active</Badge>
                          : <Badge className="bg-slate-100 text-slate-700 w-fit">Inactive</Badge>}
                        {b.is_featured && <Badge className="bg-gold-100 text-gold-700 w-fit"><Star className="w-3 h-3 mr-1 fill-current" />Featured</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/bundles/${b.slug}`, '_blank')}
                              title="View live page">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(b.id)}
                              className="text-navy-600" data-testid={`edit-bundle-${b.slug}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(b)}
                              className="text-red-600" data-testid={`delete-bundle-${b.slug}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && total > 50 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="bundles-prev-page"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {page} of {Math.ceil(total / 50)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => (p * 50 < total ? p + 1 : p))}
                  disabled={page * 50 >= total}
                  data-testid="bundles-next-page"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="bundle-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Bundle' : 'Create Bundle'}</DialogTitle>
            <DialogDescription>
              Configure the landing page sections, pricing, and webinars included in this bundle.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basics" className="mt-4">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="basics">Basics</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="webinars">Webinars ({selectedWebinarCount})</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
            </TabsList>

            {/* BASICS */}
            <TabsContent value="basics" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                         placeholder="HR Compliance & Employment Law Series" data-testid="bundle-title" />
                </div>
                <div>
                  <Label>Slug *</Label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                         placeholder="hr-compliance-employment-law" className="font-mono"
                         disabled={!!editingId} data-testid="bundle-slug" />
                  <p className="text-xs text-slate-400 mt-1">URL: /bundles/{form.slug || 'your-slug'}</p>
                </div>
              </div>
              <div>
                <Label>Subtitle</Label>
                <Textarea value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                          rows={2} placeholder="One-line value proposition for the hero section" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                         placeholder="HR Compliance & Employment Law" />
                </div>
                <div>
                  <Label>Hero Eyebrow</Label>
                  <Input value={form.hero_eyebrow} onChange={(e) => setForm({ ...form, hero_eyebrow: e.target.value })}
                         placeholder="Professional Development Bundle" />
                </div>
              </div>
              <div className="flex gap-6 pt-2">
                <label className="flex items-center gap-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                  <span className="text-sm">Active (visible to public)</span>
                </label>
                <label className="flex items-center gap-2">
                  <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} />
                  <span className="text-sm">Featured</span>
                </label>
              </div>
            </TabsContent>

            {/* CONTENT */}
            <TabsContent value="content" className="space-y-6 mt-4">
              {/* Hero Stats */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">Hero Stats (badge strip)</Label>
                  <Button size="sm" variant="outline"
                          onClick={() => addRepeaterRow('hero_stats', { value: '', label: '' })}>
                    <Plus className="w-4 h-4 mr-1" /> Add stat
                  </Button>
                </div>
                {form.hero_stats.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                    <Input value={s.value} onChange={(e) => updateRepeater('hero_stats', i, 'value', e.target.value)} placeholder="200+" />
                    <Input value={s.label} onChange={(e) => updateRepeater('hero_stats', i, 'label', e.target.value)} placeholder="Webinars" />
                    <Button size="icon" variant="ghost" onClick={() => removeRepeaterRow('hero_stats', i)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* About paragraphs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">About Paragraphs</Label>
                  <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, about_paragraphs: [...f.about_paragraphs, ''] }))}>
                    <Plus className="w-4 h-4 mr-1" /> Add paragraph
                  </Button>
                </div>
                {form.about_paragraphs.map((p, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Textarea value={p}
                              onChange={(e) => setForm((f) => {
                                const arr = [...f.about_paragraphs];
                                arr[i] = e.target.value;
                                return { ...f, about_paragraphs: arr };
                              })}
                              rows={3} className="flex-1" />
                    <Button size="icon" variant="ghost"
                            onClick={() => setForm((f) => ({ ...f, about_paragraphs: f.about_paragraphs.filter((_, idx) => idx !== i) }))}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Benefits */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">Benefits (Why Choose This)</Label>
                  <Button size="sm" variant="outline"
                          onClick={() => addRepeaterRow('benefits', { icon: '✨', title: '', description: '' })}>
                    <Plus className="w-4 h-4 mr-1" /> Add benefit
                  </Button>
                </div>
                {form.benefits.map((b, i) => (
                  <div key={i} className="grid grid-cols-[60px_1fr_auto] gap-2 mb-3 items-start">
                    <Input value={b.icon} onChange={(e) => updateRepeater('benefits', i, 'icon', e.target.value)}
                           className="text-center text-xl" />
                    <div className="space-y-1">
                      <Input value={b.title} placeholder="Always Current"
                             onChange={(e) => updateRepeater('benefits', i, 'title', e.target.value)} />
                      <Textarea value={b.description} placeholder="Description..." rows={2}
                                onChange={(e) => updateRepeater('benefits', i, 'description', e.target.value)} />
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeRepeaterRow('benefits', i)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Audience */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">Audience (Who Is This For)</Label>
                  <Button size="sm" variant="outline"
                          onClick={() => addRepeaterRow('audience', { icon: '👤', label: '' })}>
                    <Plus className="w-4 h-4 mr-1" /> Add audience
                  </Button>
                </div>
                {form.audience.map((a, i) => (
                  <div key={i} className="grid grid-cols-[60px_1fr_auto] gap-2 mb-2">
                    <Input value={a.icon} onChange={(e) => updateRepeater('audience', i, 'icon', e.target.value)}
                           className="text-center text-xl" />
                    <Input value={a.label} placeholder="HR Managers"
                           onChange={(e) => updateRepeater('audience', i, 'label', e.target.value)} />
                    <Button size="icon" variant="ghost" onClick={() => removeRepeaterRow('audience', i)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* PRICING */}
            <TabsContent value="pricing" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <h3 className="font-semibold text-navy-700">Download Access</h3>
                    <div>
                      <Label>Price *</Label>
                      <Input type="number" value={form.price_download}
                             onChange={(e) => setForm({ ...form, price_download: e.target.value })}
                             data-testid="price-download" />
                    </div>
                    <div>
                      <Label>Original Price (strikethrough)</Label>
                      <Input type="number" value={form.original_price_download}
                             onChange={(e) => setForm({ ...form, original_price_download: e.target.value })}
                             placeholder="Optional" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-gold-300">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="font-semibold text-navy-700">USB Flash Drive</h3>
                    <div>
                      <Label>Price *</Label>
                      <Input type="number" value={form.price_usb}
                             onChange={(e) => setForm({ ...form, price_usb: e.target.value })}
                             data-testid="price-usb" />
                    </div>
                    <div>
                      <Label>Original Price (strikethrough)</Label>
                      <Input type="number" value={form.original_price_usb}
                             onChange={(e) => setForm({ ...form, original_price_usb: e.target.value })}
                             placeholder="Optional" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* WEBINARS */}
            <TabsContent value="webinars" className="mt-4">
              <div className="mb-3">
                <p className="text-sm text-slate-600 mb-2">
                  Selected: <span className="font-semibold text-navy-700">{selectedWebinarCount}</span> active webinars
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Filter by title or code..."
                    value={webinarSearch}
                    onChange={(e) => setWebinarSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="border border-slate-200 rounded-lg max-h-[480px] overflow-y-auto">
                {loadingWebinars ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-navy-500" /></div>
                ) : filteredWebinarOptions.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-sm">No webinars found</div>
                ) : (
                  <ul>
                    {filteredWebinarOptions.map((w) => {
                      const checked = form.webinar_ids.includes(w.id);
                      return (
                        <li key={w.id} className={`flex items-center gap-3 p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${checked ? 'bg-navy-50' : ''}`}
                            onClick={() => toggleWebinar(w.id)}
                            data-testid={`webinar-option-${w.webinar_code}`}>
                          <input type="checkbox" checked={checked} readOnly className="rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-navy-800 truncate">{w.title}</p>
                            <p className="text-xs text-slate-500 font-mono">{w.webinar_code}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </TabsContent>

            {/* SEO */}
            <TabsContent value="seo" className="space-y-4 mt-4">
              <div>
                <Label>SEO Title</Label>
                <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
              </div>
              <div>
                <Label>SEO Description</Label>
                <Textarea value={form.seo_description}
                          onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                          rows={3} />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-bundle-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                editingId ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingId ? 'Save Changes' : 'Create Bundle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
